import React from "react";
import { Lock, Info } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { getKpiInfo } from "@/lib/kpi-explanations";
import { useTranslation } from "@/lib/i18n";

/**
 * KpiCard supports a `locked` variant + a beginner-friendly info tooltip.
 * Pass `infoKey` matching a key in `lib/kpi-explanations.js` to surface a
 * small (i) icon next to the label that explains the metric in plain English.
 *
 * Data correctness (Phase 6.Y):
 *   - Pass `rawValue` (the underlying number/null) alongside `value`
 *     (the formatted string). When `rawValue` is `null` or `undefined`,
 *     the card renders **"Data unavailable"** in a muted style instead
 *     of showing a misleading "0%" or "–". Real zeros render normally.
 *   - When `rawValue` is missing AND not locked, the (i) tooltip still
 *     works so the user can learn what the metric means even when
 *     today's data is unavailable.
 *   - Backward compatible: callers that only pass `value` keep working —
 *     missingness is only triggered when `rawValue === null/undefined`.
 */
export default function KpiCard({ label, value, helper, testId, accent, locked = false, lockedHint, infoKey, rawValue }) {
  const { locale } = useTranslation();
  const info = infoKey ? getKpiInfo(infoKey, locale) : null;

  // Treat null/undefined `rawValue` as "data unavailable". A real `0`
  // (number zero) is NOT missing and renders normally with the formatted
  // value passed in by the caller.
  const isMissing = rawValue === null || rawValue === undefined;

  if (locked) {
    return (
      <div
        data-testid={testId}
        data-locked="true"
        className="relative rounded-xl border border-border/70 bg-card/70 p-3 sm:p-4 shadow-[var(--app-shadow-elev-1)] overflow-hidden min-w-0"
        title="Available on Pro"
      >
        <div className="flex items-start justify-between gap-1.5">
          <LabelWithInfo label={label} info={info} testId={testId} />
          <span
            className="inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider shrink-0"
            style={{
              color: "#3182CE",
              background: "rgba(49,130,206,0.10)",
              border: "1px solid rgba(49,130,206,0.30)",
            }}
            data-testid={`${testId}-pro-tag`}
          >
            <Lock className="w-2.5 h-2.5" /> Pro
          </span>
        </div>
        <div className={`mt-1 font-display text-xl sm:text-2xl tabular select-none ${accent || ""}`} style={{ filter: "blur(6px)" }} aria-hidden>
          {value || "8.42x"}
        </div>
        <div className="text-[11px] sm:text-xs text-muted-foreground mt-1">{lockedHint || "Unlock with Pro"}</div>
      </div>
    );
  }
  return (
    <div data-testid={testId} data-missing={isMissing ? "true" : "false"} className="rounded-xl border border-border/70 bg-card/70 p-3 sm:p-4 shadow-[var(--app-shadow-elev-1)] min-w-0">
      <LabelWithInfo label={label} info={info} testId={testId} />
      {isMissing ? (
        <div
          className="mt-1 font-display text-base sm:text-lg tracking-tight text-muted-foreground/80 italic"
          data-testid={`${testId}-unavailable`}
          title="This metric was not available from the data provider for this stock."
        >
          Data unavailable
        </div>
      ) : (
        <div className={`mt-1 font-display text-xl sm:text-2xl tabular ${accent || ""}`}>{value}</div>
      )}
      {helper ? <div className="text-[11px] sm:text-xs text-muted-foreground mt-1">{helper}</div> : null}
    </div>
  );
}

function LabelWithInfo({ label, info, testId }) {
  if (!info) {
    return <div className="text-[10px] sm:text-[11px] uppercase tracking-wider text-muted-foreground leading-tight">{label}</div>;
  }
  return (
    <div className="flex items-start gap-1">
      <span className="text-[10px] sm:text-[11px] uppercase tracking-wider text-muted-foreground leading-tight flex-1 min-w-0">{label}</span>
      <TooltipProvider delayDuration={150}>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              aria-label={`What is ${info.title}?`}
              className="inline-flex items-center justify-center text-muted-foreground/70 hover:text-foreground transition-colors -my-1 -mr-1 p-0.5 rounded"
              data-testid={`${testId}-info`}
              onClick={(e) => e.stopPropagation()}
            >
              <Info className="w-3 h-3" />
            </button>
          </TooltipTrigger>
          <TooltipContent
            side="top"
            className="max-w-xs bg-popover text-popover-foreground border border-border shadow-[var(--app-shadow-elev-2)]"
          >
            <KpiInfoBody info={info} />
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );
}

function KpiInfoBody({ info }) {
  const { t } = useTranslation();
  return (
    <div className="space-y-1.5 text-[12px] leading-relaxed text-popover-foreground">
      <div className="font-semibold">{info.title}</div>
      <div>
        <span className="opacity-60">{t("analyze.kpi_tip.what_label")}</span>
        <span>{info.what}</span>
      </div>
      <div>
        <span className="opacity-60">{t("analyze.kpi_tip.why_label")}</span>
        <span>{info.why}</span>
      </div>
      {info.good ? (
        <div>
          <span className="opacity-60">{t("analyze.kpi_tip.rule_label")}</span>
          <span>{info.good}</span>
        </div>
      ) : null}
    </div>
  );
}
